import { Fragment, h } from "preact";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "preact/hooks";
import { useDispatch, useSelector } from "react-redux";
import sortBy from "lodash/sortBy";

import i18n from "../i18n.js";
import projectsApi from "../api/projects.js";
import {
  selectProjects,
  setProjects,
  addProjectTalent,
  addProjectTalents,
  rejectProjectTalent,
  selectProjectTalents,
} from "../store/projectsSlice.js";

import Button from "./Button.jsx";
import TextField from "./TextField.jsx";
import List from "./List.jsx";
import ListItem from "./ListItem.jsx";
import Checkbox from "./Checkbox.jsx";
import { sendError } from "../errors.js";
import ModalForm from "./ModalForm.jsx";
import { createToastMessage } from "../store/uiSlice.js";
import LinearLoadingIndicator from "./LinearLoadingIndicator.jsx";

export default function AddToProjectModal({
  admin,
  close,
  opened,
  talent,
  ...props
}) {
  const projects = useSelector(selectProjects);
  const projectTalents = useSelector(selectProjectTalents);
  const [loadedProjects, setLoadedProjects] = useState(false);
  const [loadedData, setLoadedData] = useState(false);
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(async () => {
    // Fetch projects
    try {
      const result = await projectsApi[
        admin ? "fetchAllForAdmin" : "fetchAll"
      ]();
      dispatch(setProjects(result.projects || []));
      setLoadedProjects(true);
    } catch (err) {
      console.error(err);
    }
  }, []);
  useEffect(async () => {
    // Fetch project_talents of this talent
    try {
      const result = await projectsApi.projectsForTalent(talent.id);
      dispatch(addProjectTalents(result));
      setLoadedData(true);
    } catch (err) {
      console.error(err);
    }
  }, []);

  function handleOkClick(event) {
    event && event.preventDefault();
    close();
  }

  function handleNewProjectClick() {
    if (admin) {
      window.location.href = "/admin/projects/new";
    } else {
      navigate("/projects/add");
    }
  }

  function handleSearchKeyPress(event) {
    setSearch(event.target.value.toLowerCase());
  }

  const filteredProjects = sortBy(
    search
      ? projects.filter(
          (project) =>
            (project.companyName || project.worldCompanyName || "")
              .toLowerCase()
              .indexOf(search) > -1 ||
            (project.jobTitle || "").toLowerCase().indexOf(search) > -1
        )
      : projects,
    "insertedAtOrig"
  ).reverse();

  function handleListItemClick(_event, project) {
    doChange(
      project.id,
      !(
        projectTalents[talent.id] &&
        projectTalents[talent.id].find(
          (pt) => pt.projectId === project.id && !pt.rejectedAt
        )
      )
    );
  }

  async function doChange(projectId, checked) {
    try {
      if (checked) {
        const result = await projectsApi.addTalent(projectId, talent.id);
        dispatch(addProjectTalent(result));
        createToastMessage(dispatch, {
          type: "info",
          message: i18n.t("component.add_to_project_modal.toast.added"),
        });
      } else {
        await projectsApi.removeTalent(projectId, talent.id);
        dispatch(rejectProjectTalent({ talentUserId: talent.id, projectId }));
        createToastMessage(dispatch, {
          type: "info",
          message: i18n.t("component.add_to_project_modal.toast.removed"),
        });
      }
    } catch (err) {
      console.error(err);
      sendError(
        `Could not ${checked ? "add" : "remove"} talent to/from list.`,
        { projectId, talentId: talent.id }
      );
    }
  }

  return (
    <ModalForm
      {...props}
      title={i18n.t("component.add_to_project_modal.title")}
      opened={opened}
      onClose={close}
      onSubmit={handleOkClick}
      buttonRow={
        <>
          <Button primary onClick={handleOkClick}>
            {i18n.t("actions.ok")}
          </Button>
          <Button secondary class="mr-8" onClick={handleNewProjectClick}>
            {i18n.t("component.add_to_project_modal.create_button")}
          </Button>
        </>
      }
    >
      <TextField
        slim
        placeholder={i18n.t("component.add_to_project_modal.search")}
        onChange={handleSearchKeyPress}
        disabled={projects.length === 0}
      />

      {!(loadedProjects && loadedData) ? (
        <div class="add-to-project-pullout__content mb-48">
          <LinearLoadingIndicator />
        </div>
      ) : (
        <div class="add-to-project-pullout__content mb-48">
          {projects.length === 0 ? (
            <p class="color-shades-dim-grey f-italic center">
              {i18n.t("component.add_to_project_modal.no_projects")}
            </p>
          ) : (
            <List class="add-to-project-pullout__list">
              {filteredProjects.map((project) => {
                const disabled =
                  admin &&
                  projectTalents[talent.id] &&
                  projectTalents[talent.id].find(
                    (pt) => pt.projectId === project.id && pt.rejectedAt
                  );
                return (
                  <ListItem
                    key={project.id}
                    onClick={(event) =>
                      !disabled && handleListItemClick(event, project)
                    }
                    iconFront={
                      <Checkbox
                        class="mr-8 p-0 pt-4"
                        checked={
                          projectTalents[talent.id] &&
                          projectTalents[talent.id].find(
                            (pt) =>
                              pt.projectId === project.id && !pt.rejectedAt
                          )
                        }
                        key={
                          projectTalents[talent.id] &&
                          projectTalents[talent.id].find(
                            (pt) =>
                              pt.projectId === project.id && !pt.rejectedAt
                          )
                        }
                        disabled={disabled}
                      />
                    }
                    secondLine={[
                      project.insertedAt,
                      project.companyName || project.worldCompanyName,
                      project.locations[0],
                      i18n.t(`options.workplace_type.${project.workplaceType}`),
                    ]
                      .filter((x) => x)
                      .join(" | ")}
                  >
                    {project.jobTitle}
                  </ListItem>
                );
              })}
            </List>
          )}
        </div>
      )}
    </ModalForm>
  );
}
